import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';

export type ChatHistoryItem = { id?: string; type: string; content: string };

interface UseChatComposerOptions {
  history?: ChatHistoryItem[]; // used for ArrowUp/ArrowDown to cycle human messages
  onSubmit: (text: string) => Promise<void> | void; // your streaming call
}

export const useChatComposer = (options: UseChatComposerOptions) => {
  const { history = [], onSubmit } = options;
  const [input, setInput] = useState('');
  const [previousSentMessagesIndex, setPreviousSentMessagesIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const humanMessages = useMemo(
    () => history.filter((m) => m?.type === 'human'),
    [history],
  );

  const submit = useCallback(async () => {
    const value = input;
    if (!sanitizeHtml(value)) return;
    setInput('');
    setPreviousSentMessagesIndex(null);
    await onSubmit(value);
  }, [input, onSubmit]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  const onEnter = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    void submit();
  }, [submit]);

  const onShiftEnter = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setInput((prev) => `${prev}\n`);
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, []);

  const onArrowUp = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    if (previousSentMessagesIndex === null && humanMessages.length) {
      setPreviousSentMessagesIndex(humanMessages.length - 1);
      setInput(humanMessages[humanMessages.length - 1].content);
    } else if (previousSentMessagesIndex !== null && previousSentMessagesIndex > 0) {
      setPreviousSentMessagesIndex(previousSentMessagesIndex - 1);
      setInput(humanMessages[previousSentMessagesIndex - 1].content);
    }
  }, [humanMessages, previousSentMessagesIndex]);

  const onArrowDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    if (previousSentMessagesIndex !== null && previousSentMessagesIndex < humanMessages.length - 1) {
      setPreviousSentMessagesIndex(previousSentMessagesIndex + 1);
      setInput(humanMessages[previousSentMessagesIndex + 1].content);
    } else {
      setPreviousSentMessagesIndex(null);
      setInput('');
    }
  }, [humanMessages, previousSentMessagesIndex]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) return onEnter(e);
    if (e.key === 'Enter' && e.shiftKey) return onShiftEnter(e);
    if (e.key === 'ArrowUp') return onArrowUp(e);
    if (e.key === 'ArrowDown') return onArrowDown(e);
    return undefined;
  }, [onEnter, onShiftEnter, onArrowUp, onArrowDown]);

  return {
    input,
    setInput,
    textareaRef,
    onChange,
    onKeyDown,
    submit,
  };
};
